import { useEffect, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { Field } from 'formik'
import * as Yup from 'yup'

import { useGetEmployeeEntitlement } from 'services/api/modules/leave/entitlement'

import AdxStatusBar from 'components/reusable/statusBar/AdxStatusBar'
import AdxDropdown from 'components/custom/AdxDropdown'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import FillLeaveWithEntitlement from '../components/fillLeaveWithEntitlement'
import FillLeaveWithoutEntitlement from '../components/fillLeaveWithoutEntitlement'
import AdxButton from 'components/custom/AdxButton'

const FillLeaveView = ({
  setPageState,
  setSelected,
  selectedLeave,
  formikProps,
  getValidationSchema,
  requestDateError,
}) => {
  const { data: leaveBalance } = useGetEmployeeEntitlement()
  const [disableNextButton, setDisableNextButton] = useState(false)

  let validationSchema

  if (selectedLeave.leave_policy.type === 1) {
    validationSchema = Yup.object().shape({
      selectedLeave: Yup.mixed().required('Leave must be selected'),
      shiftLeave: Yup.string().required('Shift option must be selected'),
      duration: Yup.number().when('shiftLeave', {
        is: (val) => val === 'AM' || val === 'PM',
        then: (schema) =>
          schema.max(
            1,
            ({ max }) => `You cannot request more than ${max} day when half-day option is selected`
          ),
        otherwise: (schema) =>
          // The duration schema when FULL_DAY option is selected.
          // The duration will have max value of the entitlement balance + current leave credit granted ( if any ) + carry forward amount ( if any )
          schema.max(
            selectedLeave.entitlement.balance +
              (selectedLeave.carry_forward ? selectedLeave.carry_forward.balance : 0) +
              (leaveBalance?.leave_credit[0]?.applicable_leave?.find(
                (uItem) => uItem.leave_policy_id === selectedLeave.leave_policy_id
              )
                ? leaveBalance?.leave_credit[0]?.leave_credit.balance || 0
                : 0),
            ({ max }) => `You cannot request more than ${max} days`
          ),
      }),
      startDate: Yup.mixed().required('Please select a date').nullable(),
      reason: selectedLeave.leave_policy.reason_required
        ? Yup.string().required('Reason is required')
        : Yup.string().optional(),
      file: selectedLeave.leave_policy.attachment_required
        ? Yup.mixed().required('Attachment is required').nullable()
        : Yup.mixed(),
    })
  }

  if (selectedLeave.leave_policy.type === 2) {
    validationSchema = Yup.object().shape({
      selectedLeave: Yup.mixed().required('Leave must be selected'),
      shiftLeave: Yup.string().required('Shift option must be selected'),
      duration: Yup.number().when('shiftLeave', {
        is: (val) => val === 'AM' || val === 'PM',
        then: (schema) =>
          schema.max(
            1,
            ({ max }) => `You cannot request more than ${max} day when half-day option is selected`
          ),
        otherwise: (schema) => schema,
      }),
      startDate: Yup.mixed().required('Please select a date').nullable(),
      reason: selectedLeave.leave_policy.reason_required
        ? Yup.string().required('Reason is required')
        : Yup.string().optional(),
      file: selectedLeave.leave_policy.attachment_required
        ? Yup.mixed().required('Attachment is required').nullable()
        : Yup.mixed(),
    })
  }

  useEffect(() => {
    getValidationSchema(validationSchema)
  }, [selectedLeave]) // eslint-disable-line react-hooks/exhaustive-deps

  const {
    values,
    handleChange,
    setFieldValue,
    handleSubmit,
    handleBlur,
    validateForm,
    validateField,
    errors,
    touched,
    setFieldTouched,
  } = formikProps

  useEffect(() => {
    validateForm()
  }, [getValidationSchema]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if ((values.shiftLeave === 'AM' || values.shiftLeave === 'PM') && values.duration === 1) {
      setFieldValue('duration', 0.5)
    }
    if (values.shiftLeave === 'Full Day' && values.duration === 0.5) {
      setFieldValue('duration', 1)
    }
  }, [values.duration, values.shiftLeave]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedLeave.leave_policy.half_day_option === 0 && values.shift !== 'Full Day') {
      setFieldValue('shiftLeave', 'Full Day')
    }
  }, [selectedLeave]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box
      sx={{
        minHeight: 'calc(70vh - 112px)',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {/* Form content here */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          px: 3,
          py: 2,
        }}
      >
        {/* Leave select dropdown */}
        <Field
          as={AdxDropdown}
          name="selectedLeave"
          value={values.selectedLeave}
          onChange={(e) => {
            handleChange(e)
            setSelected(JSON.parse(e.target.value))
          }}
          label={'Leave Type'}
          sx={{ width: '100%' }}
          renderValue={(option) => {
            let item = JSON.parse(option)
            return (
              <Box display="flex" alignItems="center" columnGap={2}>
                <Typography variant="body1">{item.leave_policy.name}</Typography>
                {leaveBalance?.leave_credit[0]?.applicable_leave?.find(
                  (uItem) => uItem.leave_policy_id === item.leave_policy_id
                ) && (
                  <AdxStatusBar
                    sx={{ py: 0.5, borderRadius: 1, display: { xs: 'none', mobile_l: 'block' } }}
                    status={{
                      text: 'Replacement credit utilizable',
                      color: 'white.main',
                      bgColor: 'alternate.main',
                    }}
                  />
                )}
              </Box>
            )
          }}
        >
          <Typography sx={{ p: 2 }} variant="caption" color="light-gray-text.main">
            With entitlement
          </Typography>
          {leaveBalance.with_entitlement.map((item) => (
            <AdxMenuItem
              key={item.id}
              selectedvalue={values.selectedLeave}
              value={JSON.stringify(item)}
            >
              {item.leave_policy.name}
            </AdxMenuItem>
          ))}
          <Typography sx={{ p: 2 }} variant="caption" color="light-gray-text.main">
            Without entitlement
          </Typography>
          {leaveBalance.without_entitlement.map((item) => (
            <AdxMenuItem
              key={item.id}
              selectedvalue={values.selectedLeave}
              value={JSON.stringify(item)}
            >
              {item.leave_policy.name}
            </AdxMenuItem>
          ))}
        </Field>
        {selectedLeave.leave_policy.type === 1 ? (
          <FillLeaveWithEntitlement
            selectedLeave={selectedLeave}
            leaveBalance={leaveBalance}
            values={values}
            handleChange={handleChange}
            handleBlur={handleBlur}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            errors={errors}
            touched={touched}
            validateField={validateField}
            requestDateError={requestDateError}
            setDisableNextButton={setDisableNextButton}
          />
        ) : (
          <FillLeaveWithoutEntitlement
            selectedLeave={selectedLeave}
            values={values}
            handleChange={handleChange}
            handleBlur={handleBlur}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            errors={errors}
            touched={touched}
            validateField={validateField}
            requestDateError={requestDateError}
            setDisableNextButton={setDisableNextButton}
          />
        )}
      </Box>
      {/* Button part */}
      <Box
        sx={{
          position: 'sticky',
          bottom: '0',
          width: '100%',
          display: 'flex',
          justifyContent: { xs: 'center', md: 'end' },
          alignItems: 'center',
          bgcolor: 'primary.light',
          px: 3,
          py: 2,
          gap: 1,
          borderTop: '1px solid',
          borderColor: 'grayscale.line-light',
        }}
      >
        <AdxButton
          variant={'outlined'}
          onClick={() => setPageState('SELECT_LEAVE')}
          sx={{ width: { xs: '100%', md: '100px' } }}
        >
          Back
        </AdxButton>

        <AdxButton
          disabled={disableNextButton}
          variant={'contained'}
          onClick={handleSubmit}
          sx={{ width: { xs: '100%', md: '300px' } }}
        >
          Next
        </AdxButton>
      </Box>
    </Box>
  )
}

export default FillLeaveView
